import { isChunkLoadError } from './chunk-load-error';

/** Mốc lần tự tải lại gần nhất — `sessionStorage`, riêng từng tab. */
export const STALE_CHUNK_RELOAD_KEY = 'apm.staleChunkReloadAt';

/**
 * Trong khoảng này sau một lần tự tải lại thì **không** tải lại nữa.
 *
 * Không xoá mốc khi trang nạp xong: lỗi bền (chunk hỏng thật ở bản đang chạy) thì "nạp xong ⇒ xoá ⇒
 * lỗi ⇒ tải lại" là một vòng lặp vô hạn. Giữ mốc theo thời gian nghĩa là tệ nhất một lần mỗi phút.
 */
export const STALE_CHUNK_RELOAD_COOLDOWN_MS = 60_000;

export interface StaleChunkReloadDeps {
  readonly now: () => number;
  /** Hàm, không phải giá trị: chỉ chạm `window.sessionStorage` (có thể ném) trong try/catch. */
  readonly storage: () => Pick<Storage, 'getItem' | 'setItem'>;
  readonly reload: () => void;
}

const browserDeps: StaleChunkReloadDeps = {
  now: () => Date.now(),
  storage: () => window.sessionStorage,
  reload: () => window.location.reload(),
};

/**
 * Lỗi nạp chunk ⇒ tải lại trang **một lần**; trả `true` nếu đã gọi tải lại.
 *
 * Không đọc/ghi được `sessionStorage` (trình duyệt chặn dữ liệu trang) thì **không** tải lại: không
 * có mốc thì không có gì chặn vòng lặp, và để lỗi hiện ra (thông báo "tải lại trang" sẵn có) vẫn
 * hơn một trang tự nhấp nháy mãi.
 */
export function reloadForStaleChunk(
  error: unknown,
  deps: StaleChunkReloadDeps = browserDeps,
): boolean {
  if (!isChunkLoadError(error)) return false;
  try {
    const storage = deps.storage();
    const last = Number(storage.getItem(STALE_CHUNK_RELOAD_KEY));
    const now = deps.now();
    if (Number.isFinite(last) && last > 0 && now - last < STALE_CHUNK_RELOAD_COOLDOWN_MS) {
      return false;
    }
    storage.setItem(STALE_CHUNK_RELOAD_KEY, String(now));
  } catch {
    return false;
  }
  deps.reload();
  return true;
}

/**
 * Tự phục hồi khi tab mở từ **trước** một lần deploy nạp một chunk đã không còn (APM-STALE).
 *
 * Tên chunk có hash, mỗi bản build một tên, và bản cũ bị xoá khi deploy. Tab cũ vẫn giữ `index-*.js`
 * cũ nên bấm vào một tính năng nạp lười (`lazy()`, route tách chunk của TanStack Router,
 * `lazyLibrary`) là xin một tệp không còn ⇒ "Failed to fetch dynamically imported module". Chỉ tải
 * lại trang (lấy `index.html` mới, vốn `no-cache`) mới khỏi.
 *
 * Nghe `vite:preloadError` ở `window`: từ Vite 8 helper `__vitePreload` phát sự kiện này cho cả lỗi
 * preload **lẫn** lỗi `import()` của chính chunk (`baseModule().catch(handlePreloadError)`), nên một
 * chỗ phủ mọi lazy import của bản build. Dev server không dùng helper ấy — không đổi gì ở dev.
 *
 * **Không** `preventDefault()`: lỗi vẫn đi tiếp tới chỗ đang chờ nó. Trình soạn có thay đổi chưa
 * lưu chặn `beforeunload`, người dùng bấm "Ở lại" thì trang không tải lại — khi đó chỗ gọi phải
 * nhận được lỗi thật để hiện thông báo, không phải một module `undefined`.
 */
export function installStaleChunkReload(target: Window = window): () => void {
  const onPreloadError = (event: VitePreloadErrorEvent): void => {
    reloadForStaleChunk(event.payload);
  };
  target.addEventListener('vite:preloadError', onPreloadError);
  return () => target.removeEventListener('vite:preloadError', onPreloadError);
}
